/* eslint-disable unicorn/prefer-blob-reading-methods */
import _ from 'lodash-es';
import { COMPOSED_OF } from '~/scripts/constants/common-kids';
import { getType } from '~/scripts/constants/mime';
import Defaults from '~/scripts/constants/defaults';
import Utils from '~/scripts/services/utils';
import APIV5 from '~/models/api';

/**
 * @typedef $FileUploadModel
 * @property {any[]} ctxs Context array
 * @property {any} ctx_workflow Context workflow
 * @property {ArrayBuffer} data File data
 * @property {Date} date Original file date
 * @property {boolean} haveWorkflow Has file workflow?
 * @property {string} id file identifier
 * @property {string} isa ISA class identifier
 * @property {string} kid KID identifier
 * @property {any[]} links Node links
 * @property {number} loaded Bytes uploaded
 * @property {number} minLinks Number of minimal required links
 * @property {string} name Editable file name
 * @property {Date} newDate New file date
 * @property {number} offset Packet offset
 * @property {any} pdf PDF infos
 * @property {string} prefix File name prefix
 * @property {boolean} prefixIsNeeded Is prefix required?
 * @property {number} progress File upload progress
 * @property {string} suffix File name suffix
 * @property {boolean} sync Is file synced?
 * @property {boolean} uploaded Is file uploaded?
 * @property {boolean} uploading Is file uploading?
 * @property {boolean} uploadInit Is file upload initialized?
 * @property {string} url File URL for PDF download
 * @class
 */
export default class FileUpload {
	#$q;

	#$rootScope;

	#$timeout;

	#$translate;

	#Notification;

	#User;

	#abortController;

	/**
	 *
	 * @param {import('angular').auto.IInjectorService} $injector Angular injector
	 * @param {?(File | any)} [file] File object
	 * @param {?string} [name]
	 */
	/* @ngInject */
	constructor($injector, file = null, name = null) {
		/** @type {any[]} */
		this.ctxs = [];

		/** @type {any} */
		this.ctx_workflow = null;

		/** @type {any} */
		this.data = null;

		/** @type {Date} */
		this.date = new Date();

		/** @type {boolean} */
		this.deleted = false;

		/** @type {?string} */
		this.error = null;

		/** @type {File | any} */
		this.file = file;

		/** @type {boolean} */
		this.haveWorkflow = false;

		/** @type {import('@knitiv/api-client-javascript').KUpload | string} */
		this.id = file?.id;

		/** @type {?import('@knitiv/api-client-javascript').KNode} */
		this.isa = null;

		/** @type {?import('@knitiv/api-client-javascript').KNode} */
		this.kid = null;

		/** @type {any[]} */
		this.links = [];

		/** @type {number} */
		this.loaded = 0;

		/** @type {any} */
		this.mfIsaOptions = {
			displayExpandButton: false,
			uniqueKid:           true,
			formatter:           (/** @type {any} */ item) => {
				// noinspection JSBitwiseOperatorUsage
				if (item.selectable && (item.kright & Defaults.DROIT.CREATE) === 0) {
					item.selectable = false;
					item.class = ['disabled'];
				}
				return item;
			},
		};
		this.minLinks = 0;

		/** @type {?string} */
		this.name = name || (file && file.name);

		/** @type {?Date} */
		this.newDate = null;

		/** @type {import('~/scripts/services/model/node').default | undefined} */
		this.node = undefined;

		/** @type {number} */
		this.offset = 0;

		/** @type {?import('@knitiv/api-client-javascript').KNode} */
		this.oldIsaKid = null;

		/** @type {number} */
		this.packetindex = 1;

		/** @type {any} */
		this.pdf = {}; // TODO maybe we should extend this class?

		/** @type {?string} */
		this.prefix = null;

		/** @type {boolean} */
		this.prefixIsNeeded = false;

		/** @type {number} */
		this.progress = 0;

		/** @type {any[]} */
		this.signatures = [];

		/** @type {?string} */
		this.suffix = null;

		/** @type {boolean} */
		this.sync = true;

		/** @type {boolean} */
		this.uploaded = !!file?.id;

		/** @type {string | undefined} */
		this.uploadFileId = undefined;

		/** @type {boolean} */
		this.uploading = false;

		/** @type {boolean} */
		this.uploadInit = false;

		/** @type {any} */
		this.url = null;

		/** @type {?('audio' | 'html' | 'image' | 'json' | 'mail' | 'pdf' | 'text' | 'video' | 'zip')} */
		this.viewer_engine = file instanceof File ? getType(file.type) : null;

		/**
		 * Workflow parameters
		 *
		 * @type {any}
		 */
		this.wf = {};
		/**
		 * User workflows selected
		 *
		 * @type {any[]}
		 */
		this.workflow = [];

		this.#$q = $injector.get('$q');
		this.#$rootScope = $injector.get('$rootScope');
		this.#$timeout = $injector.get('$timeout');
		this.#$translate = $injector.get('$translate');
		this.#Notification = $injector.get('Notification');
		this.#User = $injector.get('User');

		this.#abortController = new AbortController();
	}

	/**
	 * @param {any} link
	 * @returns {Promise<boolean>}
	 */
	async addLink(link) {
		let pass = true;

		if (!Array.isArray(this.links)) {
			this.links = [];
		}

		// verif doublon
		for (const l of this.links) {
			if (link?.kid && l.kid === link.kid && l.linker_kid === link.linker_kid) {
				pass = false;
				this.#Notification.error({
					title:   this.#$translate.instant('error.title'),
					message: this.#$translate.instant('error.items-already-selected'),
				});
			}
		}

		// verif cardinalité
		if (pass) {
			for (const context of this.ctxs) {
				const linkedElements = this.links.filter(l => l.isa === context.node && l.linker_kid === context.linker_kid);
				if (
					link
					&& context.node === link.isa
					&& context.linker_kid === link.linker_kid
					&& linkedElements.length + 1 > context.card.to
				) {
					pass = false;
					this.#Notification.error({
						title:   this.#$translate.instant('error.title'),
						message: this.#$translate.instant('error.too-many-items-type', { name: context.placeholder }),
					});
				}
			}
		}

		if (pass && link) {
			if (!('readonly' in link)) {
				link.readonly = false;
			}

			this.links.push(link);
			await this.getPrefix();
		}

		return pass;
	}

	deleteDocumentType() {
		this.wf.eligible = false;
		if (Array.isArray(this.wf.list)) {
			this.wf.list.length = 0;
		}
		else {
			this.wf.list = 'ALL';
		}
		this.workflow.length = 0;
		this.prefix = null;
	}

	/**
	 * @param {any} propositions
	 */
	fillFormSubmission(propositions) {
		for (const context of this.ctxs) {
			for (const proposition of propositions) {
				if (proposition.assignto.type === 'KREPRE') {
					this.file.name = proposition.str;
				}
				else if (
					proposition.assignto.path.length === 2
					&& proposition.assignto.path[1].kid === context.node
					&& proposition.assignto.path[1].linker_kid === context.linker_kid
				) {
					switch (context.type) {
					case 'K_NODE;DATE': {
						this.links.push({
							isa:        context.node,
							isa_name:   context.placeholder,
							linker_kid: '0',
							name:       Utils.parseDate(proposition.str),
						});
						break;
					}

					case 'K_NODE;DOUBLE': {
						this.links.push({
							isa:        context.node,
							isa_name:   context.placeholder,
							linker_kid: '0',
							name:       Number.parseFloat(proposition.str.replace(/\s+/, '').replace(',', '.')),
						});
						break;
					}

					default: {
						this.links.push({
							isa:        context.node,
							isa_name:   context.placeholder,
							linker_kid: '0',
							name:       proposition.str,
						});
						break;
					}
					}
				}
			}
		}
	}

	async finishUpload() {
		let date = new Date();
		if (this.newDate) {
			date = this.newDate;
		}
		else if (this.date) {
			({ date } = this);
		}

		const stringDate = typeof date === 'string' ? date : Utils.isoDate(date);

		const objectDate = {
			action:      'ADD',
			class_kid:   'K_NODE;DATE',
			linker_kid:  '0',
			objnum2:     `K_NODE;${stringDate.replaceAll('-', '')}`,
			objnum2_isa: 'K_NODE;DATE',
		};

		/** @type {any[]} */
		const linksList = [];

		/** @type {any[]} */
		const linksToCreate = [];

		/** @type {any} */
		const objectList = {};

		/** @type {any[]} */
		const linksDate = [];

		/** @type {import('@knitiv/api-client-javascript').KNode | undefined} */
		const targetNumber = `K_NODE;${this.#User.getRefNr()}`;

		// Links list ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
		for (const link of this.links) {
			if (!link.kid) {
				link.kid = `K_NODE;${this.#User.getRefNr()}`;
				linksToCreate.push(link);
			}
			linksList.push({
				action:      'ADD',
				class_kid:   link.class_kid,
				objnum2:     link.kid,
				objnum2_isa: link.isa,
				linker_kid:  link.linker_kid,
			});
			if (link.isa === 'K_NODE;DATE') {
				linksDate.push(link);
			}
		}
		linksList.push(objectDate);

		objectList[targetNumber] = {
			action:     'ADD',
			isa:        this.isa,
			LINK_LIST:  { [COMPOSED_OF]: linksList },
		};

		if (this.prefix) {
			if (this.suffix) {
				objectList[targetNumber].REPRE_LIST = [{
					action: 'ADD',
					name: this.suffix,
					lang: 'K_NODE;SUFFIX',
				}];
			}
		}
		else {
			objectList[targetNumber].REPRE_LIST = [{
				action: 'ADD',
				name: this.name,
				lang: 'K_NODE;UNIVERSEL',
			}];
		}
		// }

		// Date ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
		objectList[objectDate.objnum2] = {
			action:     'ADD',
			isa:        'K_NODE;DATE',
			REPRE_LIST: [{
				action: 'ADD',
				name: stringDate,
				lang: 'K_NODE;UNIVERSEL',
				type_kid: 'K_NODE;DATE',
			}],
		};
		for (const linkDate of linksDate) {
			const { kid } = linkDate;
			let { name } = linkDate;
			if (name instanceof Date) {
				name = name.toISOString().slice(0, 10);
			}
			objectList[kid] = {
				action:     'ADD',
				isa:        'K_NODE;DATE',
				REPRE_LIST: [
					{
						action:   'ADD',
						name,
						lang:     'K_NODE;UNIVERSEL',
						type_kid: 'K_NODE;DATE',
					},
				],
			};
		}

		for (const link of linksToCreate) {
			objectList[link.kid] = {
				action:     'ADD',
				isa:        link.isa,
				REPRE_LIST: [
					{
						action:   'ADD',
						name:     link.name,
						lang:     'K_NODE;UNIVERSEL',
						type_kid: link.type_kid,
					},
				],
			};
		}

		// Final order ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
		if (this.uploadFileId && this.uploadFileId !== 'K_UPLOAD;0') {
			const responseNodeFromFile = await APIV5.instance.nodeFromFile(
				{
					// @ts-ignore
					fileid:        this.uploadFileId,
					target_objnum: targetNumber,
					OBJECT_LIST:   objectList,
					// @ts-ignore
					tree_kid:      this.file.tree_kid,
					relpath:       this.file.tree_kid ? this.file.relpath : undefined,
				},
				{ id: 'fileupload.finish-upload' },
			);

			return (responseNodeFromFile?._NR_replace && targetNumber in responseNodeFromFile._NR_replace)
				? responseNodeFromFile._NR_replace[targetNumber]
				: false;
		}
		return false;
	}

	async getPrefix() {
		const itemInfo = {
			links: {
				CONTEXT: this.links.map(link => ({
					class_kid:  link.class_kid,
					isa:        link.isa,
					kid:        link.kid,
					linker_kid: link.linker_kid,
					name:       link.name,
				})),
				TEMPS:   typeof this.date === 'string' ? this.date : `${Utils.isoDate(this.date)}Z`,
			},
		};
		if (this.prefixIsNeeded && this.isa) {
			const result = await APIV5.instance.namePrefixGet(
				{
					isa:       this.isa,
					suffixe:   this.suffix || '',
					item_info: itemInfo,
				},
				{ id: 'fileupload.get-prefix' },
			);
			this.prefix = result.prefix || null;
		}
		else {
			this.prefix = null;
		}
	}

	async prepareFinish() {
		this.uploading = false;

		if (this.uploaded) {
			return true;
		}

		// FILE FINISH
		try {
			const kid = await this.finishUpload();
			if (kid) {
				this.uploaded = true;
				this.kid = kid;

				if (this.workflow.some(wf => wf.kid !== null && wf.kid !== undefined)) {
					// eslint-disable-next-line promise/no-nesting
					await this.workflowAffect();
					this.#Notification.info({
						message: this.#$translate.instant('import.attachement-workflow'),
					});
				}
			}
			return true;
		}
		catch (/** @type {any} */ error) {
			this.error = error.toString();
		}

		return false;
	}

	readPacket() {
		const reader = new FileReader();
		const deferred = this.#$q.defer();
		reader.addEventListener('loadend', (/** @type {any} */ event) => {
			// @ts-ignore
			this.offset += event.target.result.byteLength;
			deferred.resolve(event.target.result);
		});

		reader.addEventListener('error', deferred.reject);
		reader.readAsArrayBuffer(this.file.slice(this.offset, Defaults.packetSize + this.offset));

		return deferred.promise;
	}

	/**
	 * @param {any} link
	 * @returns {Promise<any>}
	 */
	async removeLink(link) {
		_.remove(this.links, (/** @type {any} */ l) => {
			return ('kid' in link)
				? link.kid === l.kid && link.linker_kid === l.linker_kid
				: link.name === l.name && link.linker_kid === l.linker_kid;
		});

		await this.getPrefix();
	}

	reset() {
		this.file = null;
		this.name = null;
		this.offset = 0;
		this.packetindex = 1;
	}

	/**
	 * @param {any} node
	 */
	setIsa(node) {
		this.isa = node.kid;
		this.mfIsaOptions.name = node.name;
	}

	cancel() {
		this.#abortController.abort();
		this.error = 'common.aborted';
		this.progress = 100;
	}

	hasError() {
		return this.error !== null;
	}

	/**
	 * Upload a file
	 *
	 * @param {'' | 'chat'} [module] - The module where to upload the file to
	 * @returns {Promise<boolean>} Promise result
	 */
	async upload(module = '') {
		if ('tree_kid' in this.file) {
			this.uploadFileId = 'KSYSFSROOT';
		}

		if (this.file instanceof File) {
			const extension = /(\.[^.]+)?$/.exec(this.file.name);
			const upload = await APIV5.instance.upload({
				file:       {
					buffer:    this.file,
					extension: extension && extension.length > 1 ? extension[1] : '',
					size:      this.file.size,
					name:      this.file.name,
				},
				module,
				onStart:    (/** @type {import('@knitiv/api-client-javascript').KUpload} */ id) => {
					this.uploadFileId = id;
				},
				onProgress: (/** @type {any} */ p) => {
					this.#$timeout(() => {
						this.progress = p.progress;
					});
				},
			}, {
				id: 'fileupload.upload',
				// @ts-ignore
				endpointVersion: this.#$rootScope.kds ? 3 : 2,
				signal:          this.#abortController.signal,
			});

			return upload?.status === 'END';
		}

		return true;
	}

	workflowAffect() {
		const list = [];

		for (const wf of this.workflow) {
			if (this.kid && wf.kid !== null && wf.kid !== undefined) {
				list.push({
					node_kid:        this.kid,
					wf_kid:          wf.kid,
					wfallocatorlist: wf.allocator.map((/** @type {any} */ a) => a.kid),
					wfcomment:       wf.wfcomment,
					wfstate_kid:     wf.state,
				});
			}
		}

		return APIV5.instance.wfAffect({ list }, { id: 'fileupload.workflow-affect' });
	}
}
